import { httpClient } from '@/shared/api/httpClient'
import type { PageResponse } from '@/shared/api/PageResponse'

import type {
  ChangeStatusRequest,
  CreateSessionRequest,
  SessionDetail,
  SessionPlayer,
  SessionSearchParams,
  SessionSummary,
  UpdateSessionRequest,
} from '../types/session.types'

// Relativo al baseURL del httpClient (VITE_API_BASE_URL = '/api/v1').
const BASE = '/sessions'

/**
 * Construye los query params para el search.
 * Omite valores undefined/null/'' para no mandar params vacíos.
 */
function buildSearchParams(params: SessionSearchParams = {}): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    out[key] = String(value)
  }
  return out
}

export const sessionsApi = {
  search: (params: SessionSearchParams = {}): Promise<PageResponse<SessionSummary>> =>
    httpClient
      .get<PageResponse<SessionSummary>>(BASE, { params: buildSearchParams(params) })
      .then((r) => r.data),

  /** Próximas N sesiones joinable (carrusel comunidad). Default backend = 5. */
  upcoming: (limit = 5): Promise<SessionSummary[]> =>
    httpClient
      .get<SessionSummary[]>(`${BASE}/upcoming`, { params: { limit: String(limit) } })
      .then((r) => r.data),

  getById: (id: number): Promise<SessionDetail> =>
    httpClient.get<SessionDetail>(`${BASE}/${id}`).then((r) => r.data),

  /**
   * Detalle de una sesión propia para rellenar el form de "Duplicar".
   * Endpoint owner-only bajo `/me/`: bypassa la regla de visibilidad
   * pública del `getById` (que 404a CANCELLED / scheduledAt pasada).
   * El BE valida que el caller sea el creator (403 si no).
   */
  getDuplicateSource: (id: number): Promise<SessionDetail> =>
    httpClient.get<SessionDetail>(`/me/sessions/${id}/duplicate-source`).then((r) => r.data),

  listPlayers: (id: number): Promise<SessionPlayer[]> =>
    httpClient.get<SessionPlayer[]>(`${BASE}/${id}/players`).then((r) => r.data),

  create: (body: CreateSessionRequest): Promise<SessionDetail> =>
    httpClient.post<SessionDetail>(BASE, body).then((r) => r.data),

  update: (id: number, body: UpdateSessionRequest): Promise<SessionDetail> =>
    httpClient.patch<SessionDetail>(`${BASE}/${id}`, body).then((r) => r.data),

  changeStatus: (id: number, body: ChangeStatusRequest): Promise<SessionDetail> =>
    httpClient.patch<SessionDetail>(`${BASE}/${id}/status`, body).then((r) => r.data),

  join: (id: number): Promise<SessionDetail> =>
    httpClient.post<SessionDetail>(`${BASE}/${id}/join`).then((r) => r.data),

  leave: (id: number): Promise<SessionDetail> =>
    httpClient.delete<SessionDetail>(`${BASE}/${id}/join`).then((r) => r.data),

  kickPlayer: (id: number, userId: number): Promise<SessionDetail> =>
    httpClient.delete<SessionDetail>(`${BASE}/${id}/players/${userId}`).then((r) => r.data),

  /** PUT /sessions/{id}/players/{userId}/check-in — el organizador marca/desmarca
   *  la asistencia de un player (solo eventos de tienda). Devuelve el detalle. */
  checkInPlayer: (id: number, userId: number, checkedIn: boolean): Promise<SessionDetail> =>
    httpClient
      .put<SessionDetail>(`${BASE}/${id}/players/${userId}/check-in`, { checkedIn })
      .then((r) => r.data),
}
